import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";
import { site } from "@/mocks/site";
import { submitReaddyForm } from "@/utils/formSubmit";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbjnroh653ivfvnqjg";
const MESSAGE_LIMIT = 500;

const topics = ["内容建议", "线索提供", "合作咨询", "其他问题"];

const channels = [
  {
    icon: "ri-mail-send-line",
    title: "读者来信",
    value: site.email,
    href: `mailto:${site.email}`,
    desc: "内容反馈、纠错与订阅相关的问题。",
  },
  {
    icon: "ri-megaphone-line",
    title: "线索投递",
    value: site.tipEmail,
    href: `mailto:${site.tipEmail}`,
    desc: "提供时间、地点与可核实素材，我们会先做初步核实。",
  },
  {
    icon: "ri-time-line",
    title: "响应时间",
    value: "两个工作日内",
    href: "",
    desc: "编辑团队会逐一查看，重要线索优先跟进回复。",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [message, setMessage] = useState("");

  const baseUrl = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}`;

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 瓜网",
        url: `${baseUrl}/contact`,
        inLanguage: "zh-CN",
        description: "通过表单或邮箱联系瓜网编辑部，提供内容建议、新闻线索与商务合作咨询。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        url: baseUrl,
        email: site.email,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${baseUrl}/` },
          { "@type": "ListItem", position: 2, name: "联系我们", item: `${baseUrl}/contact` },
        ],
      },
    ],
    [baseUrl],
  );

  useSeo({
    title: "联系我们 - 瓜网 | 内容建议、线索投递与商务合作",
    description:
      "联系瓜网编辑部：提交内容建议、提供娱乐新闻线索或咨询商务合作。填写表单或发送邮件，我们会在两个工作日内回复。",
    keywords: "联系瓜网,瓜网投稿,线索投递,商务合作,编辑部联系方式",
    path: "/contact",
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus("loading");
    setFeedback("");
    const result = await submitReaddyForm(SUBMIT_ADDR, form, "提交成功，我们会在两个工作日内回复你。");
    if (result.ok) {
      setStatus("success");
      setFeedback(result.message);
      setMessage("");
      form.reset();
    } else {
      setStatus("error");
      setFeedback(result.message);
    }
  };

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="mb-5 flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-foreground-700">联系我们</span>
            </nav>
            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.24em] text-primary-700">
              <span className="inline-block h-2 w-2 rounded-full bg-accent-500"></span>
              Contact
            </p>
            <h1 className="max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[2.6rem] md:leading-[1.18]">
              联系瓜网编辑部
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-700">
              有内容建议、想提供线索，或者希望与我们合作？填写下面的表单，或者直接发邮件给我们。
            </p>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950">在线留言</h2>
                <p className="mt-2 text-sm text-foreground-600">
                  带 * 的为必填项。为了能核实线索，请尽量写清时间、地点与相关物料。
                </p>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800">
                        称呼 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        placeholder="怎么称呼你"
                        autoComplete="name"
                        className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800">
                        邮箱 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800">
                      咨询类型 <span className="text-accent-600">*</span>
                    </label>
                    <select
                      id="contact-topic"
                      name="topic"
                      required
                      defaultValue=""
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="" disabled>
                        请选择
                      </option>
                      {topics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800">
                      留言内容 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      maxLength={MESSAGE_LIMIT}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="请描述你的建议或线索内容…"
                      className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    ></textarea>
                    <p className="mt-1 text-right text-xs text-foreground-500">
                      {message.length} / {MESSAGE_LIMIT} 字
                    </p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    className="form-guard-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                    >
                      {status === "loading" ? (
                        "提交中…"
                      ) : (
                        <>
                          <i className="ri-send-plane-line" aria-hidden="true"></i>
                          提交留言
                        </>
                      )}
                    </button>
                    <p className="text-xs text-foreground-500">提交即表示同意我们通过邮箱回复你。</p>
                  </div>

                  {status === "success" && (
                    <p role="status" className="flex items-center gap-2 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                      <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
                      {feedback}
                    </p>
                  )}
                  {status === "error" && (
                    <p role="alert" className="flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                      <i className="ri-error-warning-line text-lg" aria-hidden="true"></i>
                      {feedback}
                    </p>
                  )}
                </form>
              </div>
            </div>

            <aside className="lg:col-span-5">
              <h2 className="font-heading text-xl font-bold text-foreground-950">其他联系方式</h2>
              <div className="mt-5 space-y-4">
                {channels.map((item) => (
                  <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-100">
                        <i className={`${item.icon} text-lg text-primary-700`} aria-hidden="true"></i>
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="mt-1 block break-all text-sm font-medium text-primary-700 hover:text-primary-800"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="mt-1 text-sm font-medium text-foreground-800">{item.value}</p>
                        )}
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-lg bg-secondary-100/70 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                  <i className="ri-shield-check-line" aria-hidden="true"></i>
                  关于线索的说明
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-secondary-900/80">
                  我们会保护线索提供者的隐私，未经同意不会公开信息来源。涉及个人隐私或无法核实的内容，编辑部可能选择不跟进。
                </p>
                <Link
                  to="/about#principles"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-secondary-800 hover:text-secondary-900"
                >
                  查看内容原则 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}